import { MIN_GATED_WORDS, MIN_SHARED_WORD_RATIO } from './constants';
import type { TSequenceOp } from '../types';

const WORD_CHAR = '\\p{L}\\p{N}_';
const SEPARATED_NUMBER = '\\p{Nd}+(?:[.,]\\p{Nd}+)+';

/**
 * Deterministic lexical tokenizer: whitespace | separated number | word-like run | punctuation/symbols.
 * A number with decimal or grouping separators (0.078, 1,245) stays one token, so a changed value
 * diffs as a value rather than as its digits around an unchanged "0.".
 */
export const TEXT_TOKEN_REGEX = new RegExp(`\\s+|${SEPARATED_NUMBER}|[${WORD_CHAR}]+|[^\\s${WORD_CHAR}]+`, 'gu');

const WORD_TOKEN_REGEX = new RegExp(`[${WORD_CHAR}]`, 'u');

export const isWordText = (text: string): boolean => WORD_TOKEN_REGEX.test(text);

/** If shared wording covers too little of both the old and new text, show one replacement. */
export const isWholesaleRewrite = <T>(operations: readonly TSequenceOp<T>[], isWord: (item: T) => boolean): boolean => {
  let sharedWordCount = 0;
  let deletedWords = 0;
  let insertedWords = 0;

  for (const operation of operations) {
    if (operation.type === 'equal' && isWord(operation.currItem)) {
      sharedWordCount += 1;
    } else if (operation.type === 'delete' && isWord(operation.prevItem)) {
      deletedWords += 1;
    } else if (operation.type === 'insert' && isWord(operation.currItem)) {
      insertedWords += 1;
    }
  }

  // Shared + deleted reconstructs the old text; shared + inserted reconstructs the new text.
  const oldTextWordCount = sharedWordCount + deletedWords;
  const newTextWordCount = sharedWordCount + insertedWords;
  if (Math.max(oldTextWordCount, newTextWordCount) < MIN_GATED_WORDS) return false;

  // Preserve the existing full one-sided insertion/deletion path.
  if (oldTextWordCount === 0 || newTextWordCount === 0) return true;

  const oldTextRetentionIsLow = sharedWordCount < oldTextWordCount * MIN_SHARED_WORD_RATIO;
  const newTextOverlapIsLow = sharedWordCount < newTextWordCount * MIN_SHARED_WORD_RATIO;

  return oldTextRetentionIsLow && newTextOverlapIsLow;
};
